// Справка „Разделени маси" (Справки → bills-splits).
//
// Един ред = едно прехвърляне на продукти от една маса към друга: кога, кой,
// откъде накъде, колко продукта и за каква стойност. `GET /bills/splits`
// връща целия период наведнъж, затова таблицата работи в КЛИЕНТСКИ режим —
// сортирането, филтрите и редът „Общо" се смятат в браузъра.
//
// Досегашните три падащи менюта над списъка (от маса, към маса, сервитьор)
// вече са филтри В КОЛОНИТЕ. Кутийките и графиката следват СЪЩИЯ обхват като
// таблицата — както беше и досега, когато менютата ги менеха заедно.
//
// Продуктите на едно разделяне (и коментарът към него) се четат от модала:
// отговорът ги носи, но старият екран показваше само броя им.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useBillsSplits } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { BillsSplitsKpis } from "./billsSplits/BillsSplitsKpis";
import { BillsSplitsActivityCard } from "./billsSplits/BillsSplitsActivityCard";
import { BillsSplitsMobileCard } from "./billsSplits/BillsSplitsMobileCard";
import { BillSplitItemsModal } from "./billsSplits/BillSplitItemsModal";
import { useBillsSplitsColumns } from "./billsSplits/billsSplitsColumns";
import { exportBillsSplits } from "./billsSplits/billsSplitsExport";
import {
  businessDayEndFrom,
  businessDayStartFrom,
} from "../../../lib/businessDay";
import {
  buildActivityPoints,
  buildSplitRows,
  checklistOptionsOf,
  isSingleDayRange,
  summarizeSplits,
  type SplitRow,
} from "./billsSplits/billsSplitsMath";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const BillsSplitsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  // Датата живее и в query параметъра ?date= — пази се при смяна на справка.
  const [dateRange, setDateRange] = useReportsDateParam(() => [
    businessDayStartFrom(dayjs()),
    businessDayEndFrom(dayjs()),
  ], "business");
  const [searchText, setSearchText] = useState("");
  const [selectedSplit, setSelectedSplit] = useState<SplitRow | null>(null);
  // `null` = таблицата още не се е обадила (ефектът ѝ тече след първия
  // рендер). Дотогава показателите гледат целия набор, за да не мигат нули.
  const [visibleRows, setVisibleRows] = useState<SplitRow[] | null>(null);

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  const {
    data: splits = [],
    isLoading,
    isFetching,
    refetch,
  } = useBillsSplits(filters);

  const rows = useMemo(() => buildSplitRows(splits), [splits]);

  // Търсачката гледа това, което човек чете в реда: двете маси, сервитьора и
  // коментара. Въпросът обикновено е „намери ми онова прехвърляне", не
  // „стесни колона" — затова е една, а не филтър в колона.
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      [row.from_bill_name, row.to_bill_name, row.waiterLabel, row.comment ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [rows, searchText]);

  const shownRows = visibleRows ?? searchedRows;
  const totals = useMemo(() => summarizeSplits(shownRows), [shownRows]);

  const isSingleDay = isSingleDayRange(dateRange);
  const activityPoints = useMemo(
    () => buildActivityPoints(shownRows, dateRange, isSingleDay),
    [shownRows, dateRange, isSingleDay],
  );

  const fromOptions = useMemo(
    () => checklistOptionsOf(rows, (row) => row.from_bill_name),
    [rows],
  );
  const toOptions = useMemo(
    () => checklistOptionsOf(rows, (row) => row.to_bill_name),
    [rows],
  );
  const waiterOptions = useMemo(
    () => checklistOptionsOf(rows, (row) => row.waiterLabel),
    [rows],
  );

  const columns = useBillsSplitsColumns({
    fromOptions,
    toOptions,
    waiterOptions,
    isMultiDay: !isSingleDay,
  });

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.billsSplitsListKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          shownRows.length,
          "reportsPage.billsSplitsRowOne",
          "reportsPage.billsSplitsRowMany",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.billsSplitsTableHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.billsSplitsTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          {/* БЕЗ бутон „Δ спрямо предходния период": показателите тук следват
              филтрите в колоните, а предходният период не се филтрира — двете
              числа щяха да са за различни обхвати и Δ-то щеше да лъже. */}
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              dayMode="business"
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              withTime
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <BillsSplitsKpis totals={totals} />

              <BillsSplitsActivityCard
                points={activityPoints}
                isHourly={isSingleDay}
                isNarrow={narrow}
              />

              <DataTable<SplitRow>
                tableId="bills-splits"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => String(row.id)}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.billsSplitsTitle")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.billsSplitsSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportBillsSplits({ rows: shownRows, t })}
                    disabled={shownRows.length === 0}
                    bare
                  />
                }
                onRowClick={(row) => setSelectedSplit(row)}
                mobileCard={(row) => (
                  <BillsSplitsMobileCard row={row} isMultiDay={!isSingleDay} />
                )}
                initialSorting={[{ id: "split_date", desc: true }]}
                emptyTitle={t("reportsPage.noSplitsData")}
                emptyHint={t("reportsPage.billsSplitsEmptyHint")}
              />
            </>
          )}
        </div>
      </div>

      <BillSplitItemsModal
        split={selectedSplit}
        onClose={() => setSelectedSplit(null)}
      />
    </div>
  );
};
